/**
 * One place for every public page's search and share metadata.
 *
 * Used twice:
 *   1. at runtime by components/marketing/RouteSeo.jsx, so navigating the site updates the tab
 *      title, the description Google reads and the card Twitter renders;
 *   2. at build time by scripts/prerender-og.js, which writes a copy of index.html per route with
 *      these tags baked in — WhatsApp, LinkedIn, Facebook and Slack don't run JavaScript, so a
 *      share preview can only ever come from the HTML that was served.
 *
 * CommonJS on purpose: the build script needs to require it from plain Node.
 * Titles: aim for 55-60 characters. Descriptions: 140-155.
 */
const OG = {
  home: "/og/home.jpg",
  network: "/og/lending-network.jpg",
  funding: "/og/business-funding.jpg",
};

const PAGES = {
  "/": {
    title: "Connexis Group — Infrastructure for Financial Growth",
    description: "Real-time lead routing, AI voice and chat operations and enterprise compliance for UK and EU "
      + "financial brands. 240,000+ enquiries routed every month.",
    image: OG.home,
  },

  // ── Funding, for the borrower ────────────────────────────────────────────
  "/business-funding": {
    title: "Business funding, explained — Connexis",
    description: "One application, placed with the UK funders whose criteria it meets. £25,000 to £500m+, any "
      + "sector, any purpose — and nothing to pay us.",
    image: OG.funding,
  },
  "/property-finance": {
    title: "Property & development finance — Connexis",
    description: "Development finance, bridging, auction and commercial mortgages placed with UK funders. "
      + "£100k to £500m+, priced on the exit, at no cost to the borrower.",
    image: OG.funding,
  },
  "/healthcare-finance": {
    title: "Healthcare & care home finance — Connexis",
    description: "Acquisition, refinance, development and equipment funding for care homes, dental, veterinary "
      + "and clinical operators. £250k to £500m+, with specialist funders.",
    image: OG.funding,
  },
  "/manufacturing-finance": {
    title: "Manufacturing & industrial finance — Connexis",
    description: "Asset finance and refinance, invoice finance, trade finance and acquisition debt for UK "
      + "manufacturers and engineering groups. £25k to £500m+.",
    image: OG.funding,
  },
  "/hospitality-finance": {
    title: "Hospitality & leisure finance — Connexis",
    description: "Acquisition, commercial mortgages, fit-out and revenue-based funding for UK hotels, pubs, "
      + "restaurants and leisure operators. £25k to £250m+.",
    image: OG.funding,
  },
  "/apply/status": {
    title: "Track your funding application — Connexis",
    description: "Enter your reference to see exactly where your application is: read, matched, out to funders "
      + "or waiting on terms.",
    image: OG.funding,
  },

  // ── The lending network ──────────────────────────────────────────────────
  "/lending-network": {
    title: "Connexis Lending Network — for introducers and lenders",
    description: "The private network between introducers and lenders. Structured cases, criteria-driven "
      + "routing, e-signed agreements and automated commission. Invitation only.",
    image: OG.network,
  },
  "/introducer/apply": {
    title: "Become a Connexis introducer — apply",
    description: "Refer business funding cases and get paid properly: transparent commission, a portal that "
      + "shows every case live, and a desk that does the placing for you.",
    image: OG.network,
  },
  "/lender/apply": {
    title: "Join the Connexis lender panel — apply",
    description: "Receive criteria-matched, document-complete cases from vetted introducers, with capacity "
      + "controls, e-signed terms and a desk that filters before you see it.",
    image: OG.network,
  },
  "/partners": {
    title: "Partner with Connexis — introducers, lenders, publishers",
    description: "Three ways to work with us: introduce funding cases, join the lender panel, or supply "
      + "compliant enquiries as a publisher.",
    image: OG.network,
  },
  "/partner-with-us": {
    title: "Run campaigns with Connexis — call centres & media partners",
    description: "Outbound call centres and media companies: put UK businesses that need funding in front of "
      + "our lending network. Tracked attribution, approved wording, paid on funded deals.",
    image: OG.network,
  },
  "/partners/introducer": {
    title: "For introducers — Connexis partner programme",
    description: "Bespoke commission, live case tracking, automated statements and a funding desk that places "
      + "your cases across the whole panel.",
    image: OG.network,
  },
  "/partners/lender": {
    title: "For lenders — join the Connexis panel",
    description: "Criteria-driven routing, capacity caps, complete files and automated agreements. See only the "
      + "cases you can actually write.",
    image: OG.network,
  },

  // ── The platform ─────────────────────────────────────────────────────────
  "/solutions": {
    title: "Solutions — customer acquisition infrastructure | Connexis",
    description: "Lead routing, pay per call, AI voice and chat, compliance and reporting — the whole "
      + "acquisition stack for regulated financial brands, as one platform.",
  },
  "/services": {
    title: "Services — managed acquisition & operations | Connexis",
    description: "Managed campaigns, call operations, data compliance and integration work, run by the team "
      + "that built the platform underneath it.",
  },
  "/sectors": {
    title: "Sectors we work in — regulated finance | Connexis",
    description: "Live demand and routing across lending, insurance, claims, wealth, mortgages, utilities and "
      + "more. See volumes and coverage by sector.",
  },
  "/buy-leads": {
    title: "Buy financial services leads — verified & compliant | Connexis",
    description: "Buy exclusive, consent-verified enquiries with full audit trails, delivered to your CRM in "
      + "real time. Set your own filters, caps and pricing.",
  },
  "/sell-leads": {
    title: "Sell leads to Connexis — publisher programme",
    description: "Monetise financial enquiries with transparent pricing, real-time postbacks, fast payouts and "
      + "a dashboard that shows exactly what converted.",
  },
  "/pay-per-call": {
    title: "Pay per call for financial services — Connexis",
    description: "Only pay for calls that qualify: duration thresholds, buyer routing, live whisper and "
      + "recordings with every call scored against your criteria.",
  },
  "/live-calls": {
    title: "Live transfer calls — warm financial enquiries | Connexis",
    description: "Qualified consumers transferred live to your desk, screened against your criteria before the "
      + "call connects. Capacity and hours controlled by you.",
  },
  "/inbound-calls": {
    title: "Inbound call campaigns — Connexis",
    description: "Inbound demand routed to the right desk in milliseconds, with call scoring, duplicate "
      + "suppression and compliance recordings as standard.",
  },
  "/ai-systems": {
    title: "AI voice & chat operations for finance — Connexis",
    description: "AI agents that qualify, chase and book — supervised, auditable and speaking your compliance "
      + "language. Deployed on your data, not a generic model.",
  },
  "/integrations": {
    title: "Integrations — CRMs, dialers & webhooks | Connexis",
    description: "Native sync with Salesforce, HubSpot, Pipedrive, GoHighLevel and dozens more, plus webhooks, "
      + "postbacks and a documented API.",
  },
  "/enterprise": {
    title: "Enterprise — for brands & networks | Connexis",
    description: "Multi-brand routing, private panels, SSO, data residency and contracted SLAs for enterprise "
      + "financial groups.",
  },
  "/pricing": {
    title: "Pricing — leads, calls & platform | Connexis",
    description: "Transparent pricing per lead, per call and per seat, with volume tiers and no long lock-ins. "
      + "See what you'd pay before you speak to anyone.",
  },
  "/how-it-works": {
    title: "How Connexis works — routing, in plain terms",
    description: "From enquiry to routed, sold and reported in seconds: exactly how a lead moves through the "
      + "platform, step by step.",
  },
  "/benchmark-report": {
    title: "UK finance acquisition benchmark report — Connexis",
    description: "Conversion, cost per acquisition and speed-to-lead benchmarks across UK financial sectors, "
      + "drawn from live routing data.",
  },
  "/publisher/apply": {
    title: "Apply as a publisher — Connexis",
    description: "Send compliant financial enquiries and get paid on verified delivery, with postbacks, live "
      + "reporting and weekly payouts.",
  },
  "/affiliate": {
    title: "Affiliate programme — Connexis",
    description: "Earn recurring commission for introducing brands to the platform, tracked properly and paid "
      + "on time.",
  },

  // ── The firm ─────────────────────────────────────────────────────────────
  "/about": {
    title: "About Connexis Group — who we are",
    description: "A UK financial infrastructure business: the routing platform, the lending network and the "
      + "people who run the desk behind both.",
    image: OG.home,
  },
  "/why-connexis": {
    title: "Why Connexis — what makes the difference",
    description: "Speed, compliance and honesty about numbers. The reasons brands move their acquisition onto "
      + "Connexis and stay.",
  },
  "/inside-connexis": {
    title: "Inside Connexis — how the business runs",
    description: "A look at the desk, the technology and the standards behind the platform, from the team that "
      + "operates it daily.",
  },
  "/customers": {
    title: "Customers — who we work with | Connexis",
    description: "Lenders, brokers, insurers and networks using Connexis to route, qualify and convert "
      + "regulated financial demand.",
  },
  "/insights": {
    title: "Insights — UK financial acquisition analysis | Connexis",
    description: "Rate cards, routing analysis, compliance changes and market data for people buying and "
      + "selling financial demand in the UK.",
  },
  "/careers": {
    title: "Careers at Connexis Group — open roles",
    description: "Engineering, compliance, marketing and client relations roles in a UK fintech infrastructure "
      + "business that ships weekly.",
  },
  "/faq": {
    title: "Frequently asked questions — Connexis",
    description: "Pricing, compliance, data handling, integrations and contracts — the questions buyers and "
      + "partners ask before signing.",
  },
  "/contact": {
    title: "Contact Connexis Group",
    description: "Speak to the desk about buying demand, joining the panel or funding a business. Straight "
      + "answers, same working day.",
  },
  "/book-demo": {
    title: "Book a demo — see Connexis routing live",
    description: "Twenty minutes on a live platform: routing, call flow, compliance trail and reporting, using "
      + "your own criteria.",
  },
  "/changelog": {
    title: "Changelog — what shipped at Connexis",
    description: "Every release, in date order: routing changes, portal features, integrations and compliance "
      + "updates.",
  },
  "/roadmap": {
    title: "Roadmap — what's next at Connexis",
    description: "What we're building next across routing, AI operations, the lending network and partner "
      + "portals.",
  },
  "/trust": {
    title: "Trust & compliance — data, consent, security | Connexis",
    description: "How consent is captured and evidenced, where data lives, who can see it and the controls that "
      + "keep it that way.",
  },
  "/status": {
    title: "Platform status — Connexis",
    description: "Live uptime and incident history for routing, APIs, portals and call infrastructure.",
  },
  "/leaderboard": {
    title: "Partner leaderboard — Connexis",
    description: "Who is performing this month across the introducer and publisher networks, ranked on "
      + "delivered outcomes.",
  },

  // ── Legal ────────────────────────────────────────────────────────────────
  "/legal": { title: "Legal & policies — Connexis Group", description: "Terms, privacy, cookies, acceptable use, data processing and subject access — every policy in one place." },
  "/legal/privacy": { title: "Privacy policy — Connexis Group", description: "What personal data we hold, why we hold it, how long for, and the rights you have over it." },
  "/legal/cookies": { title: "Cookie policy — Connexis Group", description: "The cookies this site sets, what each one does and how to change your choices at any time." },
  "/legal/acceptable-use": { title: "Acceptable use policy — Connexis Group", description: "What is and isn't permitted when using the Connexis platform, APIs and data." },
  "/legal/terms-buyer": { title: "Buyer terms — Connexis Group", description: "The terms that apply when buying leads, calls or platform access from Connexis." },
  "/legal/terms-publisher": { title: "Publisher terms — Connexis Group", description: "The terms that apply when supplying enquiries or calls to Connexis as a publisher." },
  "/legal/terms-affiliate": { title: "Affiliate terms — Connexis Group", description: "The terms that apply to the Connexis affiliate and referral programme." },
  "/legal/dpa": { title: "Data processing agreement — Connexis Group", description: "Our standard DPA: roles, purposes, security measures, sub-processors and transfers." },
  "/legal/dsar": { title: "Data subject access requests — Connexis Group", description: "How to ask what data we hold about you, and how to have it corrected or erased." },
};

module.exports = { PAGES, OG };
